import React, { useState } from "react";
import { FiGlobe, FiPlus } from "react-icons/fi";
import { BiTrash } from "react-icons/bi";
import Modal from "@/components/modal";

const LANGUAGE_LEVELS = [
  { value: "basic", label: "Basic", badgeClass: "bg-gray-100 text-gray-700" },
  { value: "independent", label: "Independent", badgeClass: "bg-blue-100 text-blue-800" },
  { value: "proficient", label: "Proficient", badgeClass: "bg-green-100 text-green-800" },
  { value: "fluent", label: "Fluent", badgeClass: "bg-emerald-100 text-emerald-800" },
  { value: "native", label: "Native", badgeClass: "bg-purple-100 text-purple-800" },
];

function LanguageModal({ isOpen, onClose, onSave, existingLanguages }) {
  const [formData, setFormData] = useState({ name: "", level: "independent" });

  React.useEffect(() => {
    if (isOpen) {
      setFormData({ name: "", level: "independent" });
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.level) return;
    
    // Check if language already exists
    if (existingLanguages.some((l) => l.name.toLowerCase() === formData.name.toLowerCase())) {
      return;
    }
    
    onSave(formData);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-md">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Add Language</h2>
          <p className="text-sm text-gray-500 mt-1">Add a language you speak</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              placeholder="e.g. English, Spanish, French"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proficiency Level <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {LANGUAGE_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.level === level.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="level"
                    value={level.value}
                    checked={formData.level === level.value}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="sr-only"
                  />
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${level.badgeClass}`}>
                    {level.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary cursor-pointer"
            >
              Add Language
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default function LanguagesSection({ values, setValues }) {
  const [showModal, setShowModal] = useState(false);
  const languages = values.languages || [];

  const handleAdd = (language) => {
    setValues({
      ...values,
      languages: [...languages, language],
    });
  };

  const handleRemove = (index) => {
    setValues({
      ...values,
      languages: languages.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 text-teal-600 rounded-lg">
            <FiGlobe className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Languages</h2>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors cursor-pointer"
        >
          <FiPlus className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-3">
        {languages.length > 0 ? (
          languages.map((lang, index) => {
            const levelInfo = LANGUAGE_LEVELS.find((l) => l.value === lang.level);
            return (
              <div key={index} className="flex justify-between items-center group">
                <span className="font-medium text-gray-900">{lang.name}</span>
                <div className="flex items-center gap-2">
                  {levelInfo && (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${levelInfo.badgeClass}`}>
                      {levelInfo.label}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                  >
                    <BiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-gray-500 italic">No languages added yet.</p>
        )}
      </div>

      <LanguageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleAdd}
        existingLanguages={languages}
      />
    </div>
  );
}
